import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Input,
  VStack,
  HStack,
  Text,
  Box,
  Spinner,
  useToast,
  Divider,
  RadioGroup,
  Radio,
  Stack,
} from "@chakra-ui/react";
import { useFindMany, useGlobalAction } from "@gadgetinc/react";
import { api } from "../api";

const TRAINING_LEVEL_OPTIONS = [
  { value: "EMT", label: "EMT" },
  { value: "AEMT", label: "AEMT" },
  { value: "Paramedic", label: "Paramedic" },
  { value: "CriticalCare", label: "Critical Care (CCP/FP-C)" },
];

const normalizeTrainingLevel = (value) => {
  const source = String(value || "").trim().toLowerCase();
  if (source.includes("critical") || source.includes("ccp") || source.includes("fp-c") || source.includes("fpc")) return "CriticalCare";
  if (source.includes("paramedic")) return "Paramedic";
  if (source.includes("aemt") || source.includes("advanced emt") || source.includes("emt-advanced")) return "AEMT";
  if (source.includes("emt")) return "EMT";
  return "EMT";
};

const sortMembershipsByName = (memberships = []) => {
  return [...memberships].sort((left, right) => {
    const leftLast = String(left?.user?.lastName || "").trim().toLowerCase();
    const rightLast = String(right?.user?.lastName || "").trim().toLowerCase();
    if (leftLast !== rightLast) return leftLast.localeCompare(rightLast);

    const leftFirst = String(left?.user?.firstName || "").trim().toLowerCase();
    const rightFirst = String(right?.user?.firstName || "").trim().toLowerCase();
    if (leftFirst !== rightFirst) return leftFirst.localeCompare(rightFirst);

    return String(left?.user?.email || "").trim().toLowerCase().localeCompare(
      String(right?.user?.email || "").trim().toLowerCase()
    );
  });
};

export const VoiceSimShareModal = ({ isOpen, onClose, scenario, instructorCohorts }) => {
  const toast = useToast();
  
  // Helper function to calculate hours until expiration
  const calculateHoursUntilExpiration = (dateTimeString) => {
    const expiration = new Date(dateTimeString);
    const now = new Date();
    const diffMs = expiration - now;
    const hours = Math.ceil(diffMs / (1000 * 60 * 60));
    return Math.max(1, Math.min(72, hours)); // Clamp between 1-72 hours
  };
  
  // Helper function to parse emails
  const parseEmails = (emailText) => {
    return emailText
      .split(/[,\n]/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
  };
  
  // Helper function to validate email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Form state
  const [sharingMode, setSharingMode] = useState(
    instructorCohorts && instructorCohorts.length > 0 ? "cohort" : "email"
  );
  const [selectedCohortId, setSelectedCohortId] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [emailText, setEmailText] = useState("");
  const [useInstitutionalDomain, setUseInstitutionalDomain] = useState(false);
  const [institutionalDomain, setInstitutionalDomain] = useState("");
  const [expirationDate, setExpirationDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().slice(0, 16);
  });
  const [attemptsAllowed, setAttemptsAllowed] = useState(1);
  const [customMessage, setCustomMessage] = useState("");
  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  const [sendCompletionNotification, setSendCompletionNotification] = useState(true);
  const [completionInstructorIds, setCompletionInstructorIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [trainingLevel, setTrainingLevel] = useState(normalizeTrainingLevel(scenario?.levelOfCare));

  // Fetch cohort memberships when cohort is selected
  const [{ data: memberships, fetching: loadingMemberships, error: membershipsError }] = useFindMany(
    api.cohortMembership,
    {
      filter: selectedCohortId
        ? {
            cohortId: { equals: selectedCohortId },
            active: { equals: true },
          }
        : null,
      select: {
        id: true,
        userId: true,
        primaryRole: true,
        user: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
        },
      },
      first: 250,
    },
    {
      pause: !selectedCohortId,
    }
  );

  // Send invitation action
  const [{ fetching: sending, error: sendError }, sendInvitation] = useGlobalAction(
    api.sendVoiceSimInvitation
  );

  const sortedStudentMemberships = sortMembershipsByName(
    (memberships || []).filter((membership) => membership.primaryRole !== "instructor")
  );
  const sortedInstructorMemberships = sortMembershipsByName(
    (memberships || []).filter((membership) => membership.primaryRole === "instructor")
  );

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSharingMode(instructorCohorts && instructorCohorts.length > 0 ? "cohort" : "email");
      setSelectedCohortId("");
      setSelectedMemberIds([]);
      setEmailText("");
      setUseInstitutionalDomain(false);
      setInstitutionalDomain("");
      const date = new Date();
      date.setDate(date.getDate() + 7);
      setExpirationDate(date.toISOString().slice(0, 16));
      setAttemptsAllowed(1);
      setCustomMessage("");
      setSendEmailNotification(true);
      setSendCompletionNotification(true);
      setCompletionInstructorIds([]);
      setSelectAll(false);
      setTrainingLevel(normalizeTrainingLevel(scenario?.levelOfCare));
    }
  }, [isOpen, instructorCohorts, scenario?.levelOfCare]);

  // Handle cohort selection
  const handleCohortChange = (e) => {
    const cohortId = e.target.value;
    setSelectedCohortId(cohortId);
    setSelectedMemberIds([]);
    setCompletionInstructorIds([]);
    setSelectAll(false);
  };

  // Handle membership selection
  const handleMemberToggle = (memberId) => {
    setSelectedMemberIds((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedMemberIds([]);
      setSelectAll(false);
    } else {
      if (sortedStudentMemberships.length > 0) {
        setSelectedMemberIds(sortedStudentMemberships.map((membership) => membership.id));
        setSelectAll(true);
      }
    }
  };

  // Update selectAll state when memberships change
  useEffect(() => {
    if (sortedStudentMemberships.length > 0) {
      setSelectAll(selectedMemberIds.length === sortedStudentMemberships.length);
    } else {
      setSelectAll(false);
    }
  }, [selectedMemberIds, sortedStudentMemberships]);

  // Validation
  const isFormValid = () => {
    if (sharingMode === "cohort") {
      if (!selectedCohortId) return false;
      if (selectedMemberIds.length === 0) return false;
    } else if (sharingMode === "email") {
      const emails = parseEmails(emailText);
      if (emails.length === 0) return false;
      if (!emails.every(isValidEmail)) return false;
      if (useInstitutionalDomain && !institutionalDomain) return false;
    }
    if (!expirationDate) return false;
    if (new Date(expirationDate) <= new Date()) return false;
    if (attemptsAllowed < 1 || attemptsAllowed > 5) return false;
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast({
        title: "Invalid form",
        description: "Please fill out all required fields correctly.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const payload = {
        scenarioId: scenario.id,
        sharingMode: sharingMode,
        expirationHours: calculateHoursUntilExpiration(expirationDate),
        attemptsAllowed: attemptsAllowed,
        trainingLevel,
        customMessage: customMessage || undefined,
        notifyOnCompletion: sendCompletionNotification,
      };

      let invitationCount = 0;

      if (sharingMode === "cohort") {
        payload.cohortId = selectedCohortId;
        payload.memberSelection = "select";
        payload.selectedMemberIds = selectedMemberIds;
        payload.completionNotifyInstructorIds = completionInstructorIds;
        invitationCount = selectedMemberIds.length;
      } else {
        const emails = parseEmails(emailText);
        payload.emails = emails;
        payload.useInstitutionalDomain = useInstitutionalDomain;
        if (useInstitutionalDomain) {
          payload.institutionalDomain = institutionalDomain;
        }
        invitationCount = emails.length;
      }

      await sendInvitation(payload);

      toast({
        title: "Invitations sent",
        description: `Successfully sent ${invitationCount} voice simulation invitation${
          invitationCount > 1 ? "s" : ""
        }.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error sending invitations",
        description: error.message || "An unexpected error occurred.",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Share Voice Simulation</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Scenario Info */}
            <Box>
              <Text fontWeight="bold" mb={1}>
                Scenario:
              </Text>
              <Text color="gray.600">{scenario?.title || "Untitled Scenario"}</Text>
            </Box>

            <Divider />

            {/* Training Level Scope */}
            <FormControl isRequired>
              <FormLabel>Training Level for This Voice Sim</FormLabel>
              <Select value={trainingLevel} onChange={(e) => setTrainingLevel(e.target.value)}>
                {TRAINING_LEVEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Used to enforce scope-of-practice coaching during the simulation.
              </Text>
            </FormControl>

            <Divider />

            {/* Sharing Mode Selection */}
            <FormControl>
              <FormLabel>Sharing Method</FormLabel>
              <RadioGroup value={sharingMode} onChange={setSharingMode}>
                <Stack direction="row" spacing={4}>
                  <Radio value="cohort" isDisabled={!instructorCohorts || instructorCohorts.length === 0}>
                    Share with Cohort
                  </Radio>
                  <Radio value="email">Share via Email</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            <Divider />

            {/* Cohort Mode Fields */}
            {sharingMode === "cohort" && (
              <>
                {/* Cohort Selection */}
                <FormControl isRequired isInvalid={!selectedCohortId && selectedMemberIds.length > 0}>
                  <FormLabel>Select Cohort</FormLabel>
                  <Select
                    placeholder="Choose a cohort"
                    value={selectedCohortId}
                    onChange={handleCohortChange}
                  >
                    {instructorCohorts?.map((cohort) => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.displayName || cohort.name}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>Please select a cohort</FormErrorMessage>
                </FormControl>

                {/* Student Selection */}
              {selectedCohortId && (
                  <FormControl isRequired isInvalid={selectedMemberIds.length === 0}>
                    <FormLabel>Select Students</FormLabel>
                    {loadingMemberships ? (
                      <Box py={4} textAlign="center">
                        <Spinner />
                        <Text mt={2}>Loading students...</Text>
                      </Box>
                    ) : membershipsError ? (
                      <Text color="red.500">Error loading students: {membershipsError.message}</Text>
                    ) : sortedStudentMemberships.length > 0 ? (
                      <VStack align="stretch" spacing={2}>
                        <HStack justify="space-between">
                          <Button size="sm" variant="outline" onClick={handleSelectAll}>
                            {selectAll ? "Deselect All" : "Select All"}
                          </Button>
                          <Text fontSize="sm" color="gray.600">
                            {selectedMemberIds.length} of {sortedStudentMemberships.length} selected
                          </Text>
                        </HStack>
                        <Box
                          maxH="200px"
                          overflowY="auto"
                          border="1px"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={3}
                        >
                          <VStack align="stretch" spacing={2}>
                            {sortedStudentMemberships.map((membership) => (
                              <Checkbox
                                key={membership.id}
                                isChecked={selectedMemberIds.includes(membership.id)}
                                onChange={() => handleMemberToggle(membership.id)}
                              >
                                {membership.user.firstName} {membership.user.lastName}{" "}
                                <Text as="span" fontSize="sm" color="gray.500">
                                  ({membership.user.email})
                                </Text>
                              </Checkbox>
                            ))}
                          </VStack>
                        </Box>
                      </VStack>
                    ) : (
                      <Text color="gray.500">No students found in this cohort.</Text>
                    )}
                    <FormErrorMessage>Please select at least one student</FormErrorMessage>
                  </FormControl>
                )}

                {selectedCohortId && sortedInstructorMemberships.length > 0 && (
                  <FormControl>
                    <FormLabel>Completion Notification Recipients (Instructors)</FormLabel>
                    <Checkbox
                      isChecked={sendCompletionNotification}
                      onChange={(e) => setSendCompletionNotification(e.target.checked)}
                      mb={2}
                    >
                      Email completion summary when student submits
                    </Checkbox>
                    {sendCompletionNotification && (
                      <Box
                        maxH="180px"
                        overflowY="auto"
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                        p={3}
                      >
                        <VStack align="stretch" spacing={2}>
                          {sortedInstructorMemberships.map((membership) => (
                              <Checkbox
                                key={`notify-${membership.user.id}`}
                                isChecked={completionInstructorIds.includes(membership.user.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCompletionInstructorIds((prev) => [...new Set([...prev, membership.user.id])]);
                                  } else {
                                    setCompletionInstructorIds((prev) => prev.filter((id) => id !== membership.user.id));
                                  }
                                }}
                              >
                                {membership.user.firstName} {membership.user.lastName}{" "}
                                <Text as="span" fontSize="sm" color="gray.500">
                                  ({membership.user.email})
                                </Text>
                              </Checkbox>
                            ))}
                        </VStack>
                      </Box>
                    )}
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      The sharing instructor is included automatically when notifications are enabled.
                    </Text>
                  </FormControl>
                )}
              </>
            )}

            {/* Email Mode Fields */}
            {sharingMode === "email" && (
              <>
                {/* Email Addresses */}
                <FormControl isRequired isInvalid={emailText && !parseEmails(emailText).every(isValidEmail)}>
                  <FormLabel>Email Addresses</FormLabel>
                  <Textarea
                    value={emailText}
                    onChange={(e) => setEmailText(e.target.value)}
                    placeholder="Enter email addresses (one per line or comma-separated)&#10;example@email.com, another@email.com&#10;third@email.com"
                    rows={5}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {parseEmails(emailText).length} email address{parseEmails(emailText).length !== 1 ? "es" : ""} entered
                  </Text>
                  <FormErrorMessage>Please enter valid email addresses</FormErrorMessage>
                </FormControl>

                {/* Institutional Domain */}
                <FormControl>
                  <Checkbox
                    isChecked={useInstitutionalDomain}
                    onChange={(e) => setUseInstitutionalDomain(e.target.checked)}
                  >
                    Use institutional domain
                  </Checkbox>
                </FormControl>

                {useInstitutionalDomain && (
                  <FormControl isRequired>
                    <FormLabel>Institutional Domain</FormLabel>
                    <Input
                      value={institutionalDomain}
                      onChange={(e) => setInstitutionalDomain(e.target.value)}
                      placeholder="@university.edu"
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      This domain will be used for email validation
                    </Text>
                  </FormControl>
                )}
              </>
            )}

            {/* Expiration Date */}
            <FormControl isRequired>
              <FormLabel>Expiration Date & Time</FormLabel>
              <Input
                type="datetime-local"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Students will have access until this date and time
              </Text>
            </FormControl>

            {/* Attempts Allowed */}
            <FormControl isRequired>
              <FormLabel>Attempts Allowed</FormLabel>
              <NumberInput
                value={attemptsAllowed}
                onChange={(_, value) => setAttemptsAllowed(value)}
                min={1}
                max={5}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Number of times each student can attempt this simulation (1-5)
              </Text>
            </FormControl>

            {/* Custom Message */}
            <FormControl>
              <FormLabel>Custom Message (Optional)</FormLabel>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a custom message for students..."
                rows={3}
              />
            </FormControl>

            {/* Email Notification */}
            <FormControl>
              <Checkbox
                isChecked={sendEmailNotification}
                onChange={(e) => setSendEmailNotification(e.target.checked)}
              >
                Send email notification to students
              </Checkbox>
            </FormControl>

            {/* Confirmation Message */}
            {((sharingMode === "cohort" && selectedMemberIds.length > 0) ||
              (sharingMode === "email" && parseEmails(emailText).length > 0)) && (
              <Box bg="blue.50" p={3} borderRadius="md">
                <Text fontSize="sm">
                  <strong>Ready to send:</strong>{" "}
                  {sharingMode === "cohort"
                    ? `${selectedMemberIds.length} invitation${selectedMemberIds.length > 1 ? "s" : ""} will be sent.`
                    : `${parseEmails(emailText).length} invitation${parseEmails(emailText).length > 1 ? "s" : ""} will be sent.`}
                </Text>
              </Box>
            )}

            {/* Error Display */}
            {sendError && (
              <Box bg="red.50" p={3} borderRadius="md">
                <Text fontSize="sm" color="red.600">
                  Error: {sendError.message}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={sending}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={sending}
            isDisabled={!isFormValid() || sending}
          >
            Send Invitations
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
